import { supabase } from '../lib/supabase';

const reviewService = {
  /**
   * Submit a post-booking review with optional photos
   */
  async submitReview({ bookingId, professionalId, serviceType, rating, feedback, reviewerName, reviewerEmail, photos, isPortfolioEligible }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      // Insert review
      const { data: review, error: reviewError } = await supabase
        ?.from('professional_reviews')
        ?.insert({
          booking_id: bookingId || null,
          user_id: user?.id || null,
          professional_id: professionalId,
          service_type: serviceType,
          rating,
          feedback: feedback || null,
          is_portfolio_eligible: isPortfolioEligible || false,
          reviewer_name: reviewerName,
          reviewer_email: reviewerEmail || null,
        })
        ?.select()
        ?.single();

      if (reviewError) throw reviewError;

      // Upload photos if any
      const uploadedPhotos = [];
      if (photos && photos?.length > 0) {
        for (const photo of photos) {
          const fileExt = photo?.name?.split('.')?.pop();
          const fileName = `${review?.id}/${Date.now()}_${Math.random()?.toString(36)?.slice(2)}.${fileExt}`;

          const { error: uploadError } = await supabase?.storage
            ?.from('review-photos')
            ?.upload(fileName, photo, { contentType: photo?.type });

          if (!uploadError) {
            const { data: photoRecord, error: photoError } = await supabase
              ?.from('professional_review_photos')
              ?.insert({
                review_id: review?.id,
                storage_path: fileName,
                file_name: photo?.name,
                file_size: photo?.size,
                mime_type: photo?.type,
                is_portfolio_eligible: isPortfolioEligible || false,
              })
              ?.select()
              ?.single();

            if (!photoError) {
              uploadedPhotos?.push(photoRecord);
            }
          }
        }
      }

      return { data: { review, photos: uploadedPhotos }, error: null };
    } catch (error) {
      console.error('Error submitting review:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get reviews for a specific professional
   */
  async getProfessionalReviews(professionalId, limit = 20) {
    try {
      const { data, error } = await supabase
        ?.from('professional_reviews')
        ?.select(`
          *,
          professional_review_photos (*)
        `)
        ?.eq('professional_id', professionalId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching professional reviews:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Get aggregated rating for a professional
   */
  async getProfessionalRatingAggregate(professionalId) {
    try {
      const { data, error } = await supabase
        ?.from('professional_ratings_aggregate')
        ?.select('*')
        ?.eq('professional_id', professionalId)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      return { data: data || null, error: null };
    } catch (error) {
      console.error('Error fetching rating aggregate:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get aggregated ratings for multiple professionals
   */
  async getMultipleProfessionalRatings(professionalIds) {
    try {
      if (!professionalIds?.length) return { data: {}, error: null };

      const { data, error } = await supabase
        ?.from('professional_ratings_aggregate')
        ?.select('*')
        ?.in('professional_id', professionalIds);

      if (error) throw error;

      // Convert to map for easy lookup
      const ratingsMap = {};
      data?.forEach(r => {
        ratingsMap[r?.professional_id] = r;
      });

      return { data: ratingsMap, error: null };
    } catch (error) {
      console.error('Error fetching multiple ratings:', error);
      return { data: {}, error: error?.message };
    }
  },

  /**
   * Check if a booking already has a review
   */
  async checkBookingReviewed(bookingId) {
    try {
      const { data, error } = await supabase
        ?.from('professional_reviews')
        ?.select('id')
        ?.eq('booking_id', bookingId)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      return { reviewed: !!data, reviewId: data?.id || null, error: null };
    } catch (error) {
      return { reviewed: false, reviewId: null, error: error?.message };
    }
  },

  /**
   * Get public URL for a review photo
   */
  getPhotoUrl(storagePath) {
    const { data } = supabase?.storage
      ?.from('review-photos')
      ?.getPublicUrl(storagePath);
    return data?.publicUrl || null;
  },
};

export default reviewService;
